// The bounds and the retry schedule of the softphone's start sequence.
//
// Starting the phone is three network steps in a row — fetch credentials,
// open the SIP WebSocket, REGISTER — over the one link that is failing when
// any of them fail. The steps must all be bounded (an unbounded one parks the
// UI on its own stage message for as long as the request hangs) and the retry
// that follows a failure must not re-run them at a fixed interval, which turns
// one agent's brownout into sustained load on the link that is already down.

/** Bound on each SIP step: transport start, and the first REGISTER. */
export const SOFTPHONE_REGISTER_TIMEOUT_MS = 15000;
/**
 * Bound on the credentials fetch that precedes them. Same order as the SIP
 * steps on purpose: it is one GET over the same link the WebSocket handshake
 * then has to cross, so there is no reason to be more patient with it, and a
 * symmetric bound keeps the worst case of the whole sequence predictable.
 * Healthy fetches land in well under a second.
 */
export const SOFTPHONE_CREDENTIALS_TIMEOUT_MS = 15000;

/** First retries stay here — the common failure is a blip the next try clears. */
export const SOFTPHONE_RETRY_BASE_MS = 5000;
/** How many retries run at the base interval before the backoff starts. */
export const SOFTPHONE_RETRY_FAST_ATTEMPTS = 2;
/** Ceiling for a long outage: slow enough to stop hammering, fast enough that
 * an agent back from lunch is not left staring at a dead phone. */
export const SOFTPHONE_RETRY_MAX_MS = 30_000;
/**
 * Jitter only ever shortens an interval, never lengthens it: a floor-wide
 * outage must not bring every agent back in lockstep, but no retry may end up
 * slower than the flat interval this schedule replaced.
 */
const SOFTPHONE_RETRY_JITTER = 0.2;

/**
 * Delay before retry `attempt` (1 = the first retry after a failure).
 * Flat at the base interval for the first `SOFTPHONE_RETRY_FAST_ATTEMPTS`,
 * then doubling up to the ceiling, jittered downward.
 */
export function softphoneRetryDelayMs(attempt: number, random: () => number = Math.random): number {
  const doublings = Math.max(0, attempt - SOFTPHONE_RETRY_FAST_ATTEMPTS);
  const interval = Math.min(SOFTPHONE_RETRY_BASE_MS * 2 ** doublings, SOFTPHONE_RETRY_MAX_MS);
  return Math.round(interval * (1 - random() * SOFTPHONE_RETRY_JITTER));
}

/**
 * Whether this tab has a phone worth protecting from a destructive recovery
 * (a full reload). A UserAgent means sip.js built something, so a registration
 * and possibly a parked pool line exist even when no call is up.
 */
export function hasPhoneRuntime(runtime: { userAgent: unknown }, liveSession: boolean): boolean {
  return runtime.userAgent !== null || liveSession;
}

/**
 * Reject if `promise` has not settled within `timeoutMs`. For a fetch, prefer
 * an AbortSignal — this only stops *waiting*, it does not cancel the work, so
 * racing a request leaves its socket (and whatever the server is waiting on)
 * alive. Right for the sip.js steps, which have nothing to abort.
 */
export async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => { reject(new Error(timeoutMessage)); }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }
}

/**
 * Share one in-flight load between callers, but never cache a rejection.
 * Memoizing the settled promise would pin a single failed chunk fetch for the
 * life of the tab, leaving a reload as the only way to get the phone back.
 */
export function memoizeLoad<T>(load: () => Promise<T>): () => Promise<T> {
  let pending: Promise<T> | null = null;
  return () => {
    pending ??= load().catch((error: unknown) => {
      pending = null;
      throw error;
    });
    return pending;
  };
}
